import * as XLSX from 'xlsx';

export interface ExcelColumn {
  field: string;
  header: string;
}

export interface ParsedExcelData {
  headers: string[];
  rows: Record<string, any>[];
}

/**
 * Parse an Excel file and extract headers and rows
 */
export const parseExcelFile = async (file: File): Promise<ParsedExcelData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        
        if (jsonData.length === 0) {
          reject(new Error('El archivo Excel está vacío'));
          return;
        }
        
        // First row is headers
        const headers = jsonData[0].map((h: any) => String(h || '').trim());
        
        // Rest are data rows
        const rows = jsonData.slice(1)
          .filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''))
          .map(row => {
            const rowObj: Record<string, any> = {};
            headers.forEach((header, index) => {
              if (header) {
                rowObj[header] = row[index] !== undefined && row[index] !== null ? row[index] : null;
              }
            });
            return rowObj;
          });
        
        resolve({ headers, rows });
      } catch (error) {
        reject(new Error(`Error al parsear el archivo Excel: ${error instanceof Error ? error.message : 'Error desconocido'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Map Excel rows to DTO objects based on field mapping
 */
export const mapExcelToDto = <T>(
  rows: Record<string, any>[],
  fieldMapping: Record<string, string> // excelColumn -> dtoField
): T[] => {
  return rows.map(row => {
    const dto: any = {};
    
    Object.entries(fieldMapping).forEach(([excelColumn, dtoField]) => {
      if (excelColumn && dtoField) {
        const value = row[excelColumn];
        dto[dtoField] = convertValue(value, dtoField);
      }
    });
    
    return dto as T;
  });
};

/**
 * Convert Excel cell value to appropriate type
 */
const convertValue = (value: any, fieldName: string): any => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  
  // Handle booleans
  if (fieldName.toLowerCase().includes('active') || fieldName.toLowerCase().includes('activo')) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    const strValue = String(value).toLowerCase().trim();
    return strValue === 'true' || strValue === '1' || strValue === 'sí' || strValue === 'si' || strValue === 'yes';
  }
  
  // Handle numbers
  if (typeof value === 'number') {
    return value;
  }
  
  // Default: return as string
  return String(value).trim();
};

/**
 * Auto-detect field mapping based on header similarity
 */
export const autoDetectMapping = (
  excelHeaders: string[],
  entityFields: { name: string; label: string }[]
): Record<string, string> => {
  const mapping: Record<string, string> = {};
  
  excelHeaders.forEach(excelHeader => {
    const normalizedExcel = normalizeString(excelHeader);
    
    // Try to find exact match first
    let match = entityFields.find(field => 
      normalizeString(field.label) === normalizedExcel ||
      normalizeString(field.name) === normalizedExcel
    );
    
    // If no exact match, try partial match
    if (!match) {
      match = entityFields.find(field => 
        normalizedExcel.includes(normalizeString(field.label)) ||
        normalizedExcel.includes(normalizeString(field.name)) ||
        normalizeString(field.label).includes(normalizedExcel) ||
        normalizeString(field.name).includes(normalizedExcel)
      );
    }
    
    if (match) {
      mapping[excelHeader] = match.name;
    }
  });
  
  return mapping;
};

/**
 * Normalize string for comparison
 */
const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]/g, ''); // Remove special characters
};

/**
 * Validate that all required fields are mapped
 */
export const validateMapping = (
  mapping: Record<string, string>,
  requiredFields: string[]
): { valid: boolean; missingFields: string[] } => {
  const mappedFields = new Set(Object.values(mapping));
  const missingFields = requiredFields.filter(field => !mappedFields.has(field));
  
  return {
    valid: missingFields.length === 0,
    missingFields
  };
};
